/**
 * The `GET /auth/session` payload, shared by the API and the web client
 * (plan §6.6).
 *
 * Before this module the shape existed twice and the two copies were bound by
 * nothing: `apps/api/src/routes/auth.ts` emitted an object literal, and
 * `apps/web/src/auth/auth-context.tsx` hand-narrowed the same fields in a local
 * type guard. Renaming `handle` on the server left BOTH suites green while the
 * SPA read every live session as unparseable and — because that guard fails
 * closed, correctly — redirected an authenticated user to `/login` on every
 * boot. No test failure, no error, no way to tell from either side alone.
 * `web -> api` is a forbidden edge (plan §3.5a) and `web -> contracts` /
 * `api -> contracts` are both allowed, so one symbol closes the drift without
 * touching the dependency rules. Same argument, same remedy as
 * `PASSWORD_MIN_LENGTH` in `./auth-policy` (ADR-0027).
 *
 * The server remains the authority on what a session is; this is the shape it
 * emits, not a second opinion about it.
 */
import { z } from 'zod';

import { ApiResponseSchema, type ApiResponseSchemaOf } from './api.schema';

/**
 * `authenticated` is `z.literal(true)`, not `z.boolean()`.
 *
 * The API answers an absent session with **401** and the uniform error body —
 * it never emits a 200 carrying `authenticated: false` (plan §6.8, security §3).
 * Modelling the field as a boolean would invent a "successfully signed out"
 * success-path shape that no route produces, and hand any broken or hostile
 * upstream a 200 that the client must then reason about. Here it is
 * unrepresentable: the negative case has exactly one encoding, and it is a 401.
 */
export const AuthSessionSchema = z.object({
  authenticated: z.literal(true),
  /*
   * Trim first: min(1) alone accepts whitespace as a proven identity.
   *
   * `.trim()` is a TRANSFORM, so this is the one field where the parsed value
   * can differ from the bytes on the wire — which would be a client inventing
   * an identity the server never asserted. It is not, because the SERVER
   * normalizes at the write boundary: `bootstrapIdentity` trims before it
   * stores (apps/api/src/routes/auth.ts), so a padded or blank handle is
   * unrepresentable in `auth.app_user` and this arm can only ever fire on a row
   * created before that did. Keeping the transform rather than rejecting is
   * deliberate: a reject would turn such a legacy row into a permanent
   * redirect-to-login loop, converting a cosmetic data problem into a lockout.
   *
   * The type annotation on the API side proves field NAMES only — `z.string()`
   * and `z.string().trim().min(1)` are both `string` — so the producer-side
   * normalization is what actually closes the gap, not this line.
   */
  handle: z.string().trim().min(1),
});

export type AuthSession = z.infer<typeof AuthSessionSchema>;

/**
 * The full envelope, so the client parses `success` and `data` in ONE call.
 *
 * `ApiResponseSchema` is a discriminated union on `success`, which is what
 * makes this better than checking the envelope by hand and then the payload: a
 * `success: false` body narrows into the error arm instead of falling through
 * the happy path with an absent `data`.
 */
export const AuthSessionResponseSchema: ApiResponseSchemaOf<typeof AuthSessionSchema> =
  ApiResponseSchema(AuthSessionSchema);
